"use client";

import React, { useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import sal from "sal.js";
import Loading from "@/components/Common/Loading";
import Link from "next/link";
import { Turnstile } from "@marsidev/react-turnstile";

const Contact = () => {
  // Obtener la sesión actual y el estado de carga
  const { data: session, status } = useSession();

  // Estados locales para el formulario
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  // Estados para los campos del formulario
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  // Turnstile
  const turnstileRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState(null);

  const apiUrl = process.env.NEXT_PUBLIC_API_URL;
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  // Inicializa las animaciones de Sal.js al montar el componente
  useEffect(() => {
    sal();
  }, []);

  // Prellenar los campos de nombre y correo si el usuario tiene sesión
  useEffect(() => {
    if (session) {
      setName(session.user?.name || "");
      setEmail(session.user?.email || "");
    }
  }, [session]);

  // Re-inicializa Sal.js una vez que termina de cargar la sesión
  useEffect(() => {
    if (status !== "loading") sal();
  }, [status]);

  /**
   * Maneja el envío del formulario de contacto.
   * Recopila los datos y los envía vía POST al endpoint correspondiente.
   *
   * @param {Event} e - Evento del formulario.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlertMessage("");

    if (!turnstileToken) {
      setAlertMessage(
        "Verificación de seguridad pendiente. Intenta nuevamente."
      );
      // intenta regenerar token por si el widget quedó “quieto”
      turnstileRef.current?.reset?.();
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${apiUrl}/contact/send-message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          message,
          turnstile_token: turnstileToken,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.message || data.detail || "Error al enviar el mensaje."
        );
      }

      setAlertMessage(data.message || "¡Mensaje enviado con éxito!");

      // reset del form (mensaje siempre)
      setMessage("");

      // reset nombre/email solo si NO hay sesión
      if (!session) {
        setName("");
        setEmail("");
      }
    } catch (error) {
      console.error(error);
      setAlertMessage(
        error?.message ||
          "Hubo un problema al enviar el mensaje. Por favor, inténtalo de nuevo."
      );
    } finally {
      setIsSubmitting(false);

      // Regenerar Turnstile para permitir otro envío
      setTurnstileToken(null);
      turnstileRef.current?.reset?.();
    }
  };

  // Mostrar el componente de carga mientras se verifica la sesión
  if (status === "loading") return <Loading />;

  return (
    <div className="rainbow-pricing-area rainbow-section-gap">
      <div className="container-fluid">
        <div className="wrapper rainbow-contact-area">
          <div className="container pl--0 pr--0">
            <div className="row">
              <div className="col-lg-12">
                <div
                  className="section-title text-center"
                  data-sal="slide-up"
                  data-sal-duration="400"
                  data-sal-delay="150"
                >
                  <h2 className="subtitle text-2xl font-bold">
                    <span className="theme-gradient">Contacto</span>
                  </h2>
                  <p className="description text-lg">
                    Estamos aquí para ayudarte con TutorIA+, ya sea para
                    resolver dudas, recibir sugerencias o conversar sobre cómo
                    crear clases más rápido.
                  </p>
                </div>
              </div>
            </div>

            {/* Formulario de contacto */}
            <div className="row mt--40 row--15">
              <div className="col-lg-8">
                <div className="contact-details-box">
                  <form
                    onSubmit={handleSubmit}
                    className="rbt-profile-row rbt-default-form row row--15"
                  >
                    {alertMessage && (
                      <div className="col-12">
                        <div className="form-group">
                          <span className="alert-message">{alertMessage}</span>
                        </div>
                      </div>
                    )}

                    <h4 className="pl--15">Cuéntanos cómo podemos ayudarte</h4>
                    <div className="rbt-sm-separator mt-0"></div>

                    <div className="col-lg-6 col-md-6 col-sm-6 col-12">
                      <div className="form-group">
                        <label htmlFor="name">Nombre</label>
                        <input
                          id="name"
                          type="text"
                          placeholder="Ingresa tu nombre"
                          maxLength="100"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    <div className="col-lg-6 col-md-6 col-sm-6 col-12">
                      <div className="form-group">
                        <label htmlFor="email">Correo electrónico</label>
                        <input
                          id="email"
                          type="email"
                          placeholder="Ingresa tu correo electrónico"
                          maxLength="100"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="message">Mensaje</label>
                        <textarea
                          id="message"
                          cols="20"
                          rows="5"
                          placeholder="Cuéntanos tu duda, sugerencia o lo que te gustaría mejorar en TutorIA+."
                          minLength="10"
                          maxLength="5000"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <Turnstile
                        ref={turnstileRef}
                        siteKey={siteKey}
                        onSuccess={(token) => setTurnstileToken(token)}
                        onExpire={() => setTurnstileToken(null)}
                        onError={() => setTurnstileToken(null)}
                      />
                    </div>

                    <div className="col-12 mt--20">
                      <div className="form-group mb--0">
                        <button
                          type="submit"
                          className="btn-default w-100"
                          disabled={isSubmitting || !turnstileToken}
                        >
                          Enviar mensaje{" "}
                          {isSubmitting && (
                            <i className="feather-loader spinner-icon"></i>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              {/* Información de contacto adicional */}
              <div className="col-lg-4 mt_md--30 mt_sm--30">
                <div className="rainbow-address">
                  <div className="icon">
                    <i className="fa-sharp fa-regular fa-envelope"></i>
                  </div>
                  <div className="inner">
                    <h4 className="title">Correo de contacto</h4>
                    <p className="b2">
                      <Link href="mailto:contacto@tutoriaplus.com">
                        contacto@tutoriaplus.com
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
